import { API } from 'src/utils/interceptor'
import { store } from 'src/features'
import { fetchAllRecipes } from 'src/features/RecipeBuilderSlice'
import { fetchVenueProducts } from 'src/features/VenueProductsSlice'
import allergenEventEmitter from 'src/utils/allergenEventEmitter'

// Canonical allergen keys expected by the backend
export const ALLERGEN_KEY_MAP = {
  celery: 'celery',
  gluten: 'gluten',
  crustaceans: 'crustaceans',
  eggs: 'eggs',
  fish: 'fish',
  lupin: 'lupin',
  milk: 'milk',
  molluscs: 'molluscs',
  mustard: 'mustard',
  nuts: 'nuts',
  peanuts: 'peanuts',
  // UI often has "Sesame seeds" – backend should be snake_case:
  'sesame seeds': 'sesame_seeds',
  sesame_seeds: 'sesame_seeds',
  // UI uses UK spelling "Soya"
  soya: 'soya',
  soy: 'soya', // just in case
  sulphites: 'sulphites',
}

export function normalizeAllergenKey(labelOrKey) {
  if (!labelOrKey) return null
  const k = String(labelOrKey).trim().toLowerCase()
  return ALLERGEN_KEY_MAP[k] || null
}

export function normalizeAllergenValue(v) {
  if (v == null) return 'no'
  const s = String(v).trim().toLowerCase()
  if (s === 'yes' || s === 'y' || s === 'true' || s === '✓') return 'yes'
  if (s === 'traces' || s === 'trace' || s === 'maybe') return 'traces'
  return 'no'
}

// Reverse mapping from canonical keys to display names
const CANONICAL_TO_DISPLAY_MAP = {
  celery: 'Celery',
  gluten: 'Gluten',
  crustaceans: 'Crustaceans',
  eggs: 'Eggs',
  fish: 'Fish',
  lupin: 'Lupin',
  milk: 'Milk',
  molluscs: 'Molluscs',
  mustard: 'Mustard',
  nuts: 'Nuts',
  peanuts: 'Peanuts',
  sesame_seeds: 'Sesame seeds',
  soya: 'Soya',
  sulphites: 'Sulphites',
}

export function canonicalToDisplayName(canonicalKey) {
  return CANONICAL_TO_DISPLAY_MAP[canonicalKey] || canonicalKey
}

// Build a snapshot index from products we render (so we can diff)
export function buildOriginalSnapshot(productsArray) {
  // productsArray: list we display in the Product Allergens table
  // We index by venueProductId → { allergenKey: "yes|no|traces", ... }
  const byProduct = {}

  for (const p of productsArray || []) {
    const venueProductId = p._id || p.venueProductId
    if (!venueProductId) continue

    const out = {}
    // App data can be in either `allergens` + `allergenTraces` arrays or a flat map.
    const hasArrays =
      Array.isArray(p.allergens) || Array.isArray(p.allergenTraces)

    if (hasArrays) {
      const yesSet = new Set(
        (p.allergens || []).map(a => normalizeAllergenKey(a)),
      )
      const traceSet = new Set(
        (p.allergenTraces || []).map(a => normalizeAllergenKey(a)),
      )
      const ALL = [
        'celery',
        'gluten',
        'crustaceans',
        'eggs',
        'fish',
        'lupin',
        'milk',
        'molluscs',
        'mustard',
        'nuts',
        'peanuts',
        'sesame_seeds',
        'soya',
        'sulphites',
      ]
      for (const key of ALL) {
        out[key] = yesSet.has(key) ? 'yes' : traceSet.has(key) ? 'traces' : 'no'
      }
    } else {
      // If server already returns normalized map
      for (const [k, v] of Object.entries(p.allergenStatus || {})) {
        out[k] = normalizeAllergenValue(v)
      }
    }

    byProduct[venueProductId] = out
  }

  return { byProduct }
}

/**
 * Bulk update for venue product allergens.
 * @param {string} venueId
 * @param {Array<{ venueProductId: string, allergenKey: string, value: string }>} changes
 */
export async function bulkUpdateAllergenAssignments(venueId, changes) {
  // Validate & normalize before POST
  const assignments = changes.map((c, idx) => {
    const targetId = c.venueProductId || c.productId || c.id
    const allergen = normalizeAllergenKey(
      c.allergenKey || c.allergen || c.label,
    )
    const status = normalizeAllergenValue(c.value)

    if (!targetId) {
      throw new Error(`Missing venueProductId for change at index ${idx}`)
    }
    if (!allergen) {
      throw new Error(`Unknown allergen key "${c.allergenKey}" at index ${idx}`)
    }
    if (!['yes', 'no', 'traces'].includes(status)) {
      throw new Error(`Invalid allergen value "${c.value}" at index ${idx}`)
    }

    return {
      itemType: 'venueProduct', // we are on Product Allergens page
      itemId: targetId,
      allergen, // canonical backend key
      value: status, // 'yes' | 'no' | 'traces'
    }
  })

  // Helpful console for debugging (leave it)
  // eslint-disable-next-line no-console
  console.log(
    'AllergenUpdateService: Bulk updating',
    assignments.length,
    'assignments',
    assignments,
  )

  const res = await API.post(
    'oms',
    `/api/v1/venues/${venueId}/allergens/bulk-update`,
    {
      body: { assignments },
    },
  )

  return res
}

class AllergenUpdateService {
  /**
   * Update allergen assignments using the unified bulk update API
   * @param {string} venueId - The venue ID
   * @param {Array} assignments - Array of allergen assignments
   * @returns {Promise} - API response
   */
  static async bulkUpdateAllergenAssignments(venueId, assignments) {
    try {
      console.log(
        'AllergenUpdateService: Bulk updating allergen assignments:',
        assignments.length,
        'assignments',
      )

      // Log the raw assignments to see what we're receiving
      console.log(
        'Raw assignments received:',
        JSON.stringify(assignments, null, 2),
      )

      // Transform assignments to match backend contract
      const transformedAssignments =
        this.transformAssignmentsForBackend(assignments)

      // Backend expects assignments directly in the body, venueId comes from URL params
      const body = { assignments: transformedAssignments }

      // Log the final payload being sent
      console.log(
        'Payload being sent to backend:',
        JSON.stringify(body, null, 2),
      )

      const response = await API.post(
        'api2',
        `venues/${venueId}/allergens/bulk-update`,
        {
          body,
        },
      )

      // console.log('AllergenUpdateService: Bulk update successful:', response)

      // Emit real-time events for each assignment
      assignments.forEach(assignment => {
        if (assignment.itemType === 'venueProduct') {
          allergenEventEmitter.notifyProductAllergenUpdate(
            assignment.itemId,
            assignment.allergen,
            assignment.value,
          )
        } else if (assignment.itemType === 'customProduct') {
          allergenEventEmitter.notifyCustomIngredientAllergenUpdate(
            assignment.itemId,
            assignment.allergen,
            assignment.value,
          )
        }
      })

      // Emit general data refresh event
      allergenEventEmitter.notifyDataRefresh(venueId)

      return response
    } catch (error) {
      console.error('AllergenUpdateService: Error in bulk update:', error)
      throw error
    }
  }

  /**
   * Transform assignments to match backend contract
   * @param {Array} assignments - Array of allergen assignments
   * @returns {Array} - Transformed assignments for backend
   */
  static transformAssignmentsForBackend(assignments) {
    // The backend expects the format: { itemType, itemId, allergen, value }
    // Our current format is already correct, just ensure values are lowercase
    return assignments.map(assignment => ({
      itemType: assignment.itemType,
      itemId: assignment.itemId,
      allergen: assignment.allergen,
      value: assignment.value.toLowerCase(),
    }))
  }

  /**
   * Refresh all allergen-related data after updates
   * @param {string} venueId - The venue ID
   */
  static async refreshAllAllergenData(venueId) {
    try {
      console.log(
        'AllergenUpdateService: Refreshing all allergen data for venue:',
        venueId,
      )

      // Refresh recipes (which contain allergen data)
      await store.dispatch(fetchAllRecipes(venueId))

      // Refresh venue products (which contain allergen data)
      await store.dispatch(fetchVenueProducts(venueId))

      console.log(
        'AllergenUpdateService: All allergen data refreshed successfully',
      )
    } catch (error) {
      console.error(
        'AllergenUpdateService: Error refreshing allergen data:',
        error,
      )
      throw error
    }
  }

  /**
   * Convert legacy allergen format to unified format
   * @param {string} itemType - 'venueProduct' or 'customProduct'
   * @param {string} itemId - The item ID
   * @param {Array} allergens - Array of allergen names
   * @param {Array} allergenTraces - Array of allergen trace names
   * @returns {Array} - Array of unified allergen assignments
   */
  static convertToUnifiedFormat(
    itemType,
    itemId,
    allergens = [],
    allergenTraces = [],
  ) {
    // Validate inputs
    if (!itemId) {
      console.warn(
        'AllergenUpdateService: convertToUnifiedFormat called with undefined itemId:',
        {
          itemType,
          itemId,
          allergens,
          allergenTraces,
        },
      )
      return []
    }

    if (!itemType) {
      console.warn(
        'AllergenUpdateService: convertToUnifiedFormat called with undefined itemType:',
        {
          itemType,
          itemId,
          allergens,
          allergenTraces,
        },
      )
      return []
    }

    const allergenTypes = [
      'Celery',
      'Gluten',
      'Crustaceans',
      'Eggs',
      'Fish',
      'Lupin',
      'Milk',
      'Molluscs',
      'Mustard',
      'Nuts',
      'Peanuts',
      'Sesame seeds',
      'Soya',
      'Sulphites',
    ]

    return allergenTypes.map(allergen => {
      let value = 'no'
      if (allergens.includes(allergen)) {
        value = 'yes'
      } else if (allergenTraces.includes(allergen)) {
        value = 'traces'
      }

      return {
        itemType,
        itemId,
        allergen,
        value,
      }
    })
  }

  /**
   * Get allergen assignments for a specific item
   * @param {string} venueId - The venue ID
   * @param {string} itemType - 'venueProduct' or 'customProduct'
   * @param {string} itemId - The item ID
   * @returns {Promise} - Allergen assignments
   */
  static async getAllergenAssignments(venueId, itemType, itemId) {
    // Validate inputs
    if (!venueId || !itemType || !itemId) {
      const error = new Error(
        'Missing required parameters for getAllergenAssignments',
      )
      console.error(
        'AllergenUpdateService: Error fetching allergen assignments:',
        {
          error: error.message,
          venueId,
          itemType,
          itemId,
        },
      )
      throw error
    }

    try {
      const response = await API.get(
        'api2',
        `venues/${venueId}/allergens/${itemType}/${itemId}`,
      )
      return response
    } catch (error) {
      console.error(
        'AllergenUpdateService: Error fetching allergen assignments:',
        error,
      )
      throw error
    }
  }

  /**
   * Update a single allergen assignment
   * @param {string} venueId - The venue ID
   * @param {string} itemType - 'venueProduct' or 'customProduct'
   * @param {string} itemId - The item ID
   * @param {string} allergen - The allergen name
   * @param {string} value - 'yes', 'traces', or 'no'
   */
  static async updateSingleAllergen(
    venueId,
    itemType,
    itemId,
    allergen,
    value,
  ) {
    const assignment = {
      itemType,
      itemId,
      allergen,
      value,
    }

    return await this.bulkUpdateAllergenAssignments(venueId, [assignment])
  }
}

export default AllergenUpdateService
